'use client'

import { useWalletPortfolioChart } from '@repo/services'
import { FeatureFlag } from '../feature-flag'
import { LineChart, getCssVar, withAlpha, type LineConfig } from '../'
import { useFeatureFlag } from '@repo/shared/hooks'
import { Col, Row, Skeleton } from '@repo/ui/components'
import {
  SegmentedGroup,
  SegmentedGroupItem,
} from '@repo/ui/components/segmented-group'
import { useIsDesktop } from '@repo/ui/hooks'
import type { UTCTimestamp } from 'lightweight-charts'
import { useEffect, useMemo, useState } from 'react'

const toUnixTimestamp = (isoUtcString: string): UTCTimestamp =>
  Math.floor(new Date(isoUtcString).getTime() / 1000) as UTCTimestamp

export const TransactionsChart = ({ currency }: { currency?: string }) => {
  const [range, setRange] = useState<string>()
  const isDesktop = useIsDesktop()

  const { getBaseTimeRanges } = useFeatureFlag(
    ['charts-token-history', 'value', 'intervals'],
    {
      returnBoolean: false,
    },
  )

  const baseTimeRanges = useMemo(
    () => getBaseTimeRanges?.() || [],
    [getBaseTimeRanges],
  )

  useEffect(() => {
    if (!range && baseTimeRanges.length > 0) {
      setRange(baseTimeRanges[0]?.value || '1d')
    }
  }, [baseTimeRanges, range])

  const { data, isLoading } = useWalletPortfolioChart({
    period: range as '1h' | '1d' | '1w' | '1M',
    orgCurrency: currency,
  })

  // LineChart expects { time, value }
  const lineData = useMemo(() => {
    const raw = data?.data?.data || []

    // Default: map 1:1
    return raw.map((item) => ({
      time: toUnixTimestamp(new Date(item.time ?? 0).toISOString()),
      value: item.availableBalance ? item.availableBalance / 10 : 0,
    }))
  }, [data?.data?.data])

  // Check if first value is greater than last value
  const isFirstValueGreater = useMemo(() => {
    if (lineData.length < 2) return false
    const firstValue = lineData[0]?.value || 0
    const lastValue = lineData[lineData.length - 1]?.value || 0
    return firstValue > lastValue
  }, [lineData])

  // Multiple lines configuration
  const linesConfig: LineConfig[] = useMemo(() => {
    if (lineData.length === 0) return []
    const color =
      getCssVar(
        isFirstValueGreater
          ? '--color-negative-main-dark'
          : '--color-positive-main-dark',
      ) || '#3B82F6' // Blue
    return [
      {
        id: 'main-line',
        data: lineData,
        label: '',
        color,

        lineType: 'solid' as const,
        hasGradient: true,
        gradientTopColor: withAlpha(color, 0.24),
        gradientBottomColor: withAlpha(color, 0),
        lineWidth: 2,
        visible: true,
      },
    ]
  }, [lineData, isFirstValueGreater])

  return (
    <div className='w-full'>
      <Row className=' justify-center w-full items-center mb-md'>
        <SegmentedGroup
          value={range}
          onValueChange={setRange}
          color='neutral'
          size='2xs'
        >
          {baseTimeRanges.map((item) => (
            <SegmentedGroupItem key={item.value} value={item.value.toString()}>
              {item.label}
            </SegmentedGroupItem>
          ))}
        </SegmentedGroup>
      </Row>
      <Col className='flex-1 ltr min-h-[200px]'>
        {isLoading ? (
          <Skeleton className='w-full min-h-[200px] max-h-[200px] sm:h-full' />
        ) : (
          <FeatureFlag flagKey='charts-token-history'>
            <LineChart
              className='w-full min-h-[200px] max-h-[200px] sm:h-full'
              lines={linesConfig}
              hidePrice
              hideTime
              formatPriceCompact={!isDesktop}
            />
          </FeatureFlag>
        )}
      </Col>
    </div>
  )
}
